import React, { useEffect, useState } from 'react';

import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';
import {
  getPendingVerifications,
  getVerificationDetail,
  decideVerification,
  fetchDocumentBlobUrl,
  VerificationSummary,
  VerificationDetail,
  VerificationDocument,
} from '../services/verificationApi';

function currentUserRole(): string | null {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw).role : null;
  } catch {
    return null;
  }
}

function DocumentLink({ doc }: { doc: VerificationDocument }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const isImage = /\.(png|jpe?g)$/i.test(doc.doc_filename);

  async function load() {
    try {
      const url = await fetchDocumentBlobUrl(doc.doc_url);
      setBlobUrl(url);
    } catch {
      setError(true);
    }
  }

  useEffect(() => {
    if (isImage) load();
  }, [isImage]);

  if (error) return <span className="muted">Could not load {doc.doc_filename}</span>;

  if (isImage) {
    return blobUrl ? (
      <a href={blobUrl} target="_blank" rel="noreferrer">
        <img src={blobUrl} alt={doc.doc_filename} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }} />
      </a>
    ) : (
      <span className="muted">Loading preview…</span>
    );
  }

  return (
    <button type="button" className="smallBtn" onClick={load}>
      {blobUrl ? (
        <a href={blobUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
          Open {doc.doc_filename}
        </a>
      ) : (
        `Load ${doc.doc_filename}`
      )}
    </button>
  );
}

function ApplicationDetail({ id, onDecided }: { id: number; onDecided: () => void }) {
  const [detail, setDetail] = useState<VerificationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    getVerificationDetail(id)
      .then(setDetail)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  async function decide(status: 'approved' | 'rejected') {
    setDeciding(true);
    setError(null);
    try {
      await decideVerification(id, status);
      onDecided();
    } catch (err: any) {
      setError(err?.message || 'Could not update the application.');
      setDeciding(false);
    }
  }

  if (loading) return <p className="muted">Loading application…</p>;
  if (!detail) return <p className="error">{error || 'Application not found.'}</p>;

  const { application_data } = detail;

  return (
    <div style={{ marginTop: 16 }}>
      <div className="missionList">
        {Object.entries(application_data)
          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
          .map(([key, value]) => (
            <div key={key} className="missionItem">
              <span className="missionDot" aria-hidden="true" />
              <span>
                <strong>{key}:</strong> {Array.isArray(value) ? value.join(', ') : String(value)}
              </span>
            </div>
          ))}
      </div>

      {detail.documents.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div className="kicker">Uploaded documents</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {detail.documents.map((doc) => (
              <DocumentLink key={doc.id} doc={doc} />
            ))}
          </div>
        </div>
      )}

      {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button type="button" className="btn btnPrimary" disabled={deciding} onClick={() => decide('approved')}>
          Approve
        </button>
        <button type="button" className="btn btnWarn" disabled={deciding} onClick={() => decide('rejected')}>
          Reject
        </button>
      </div>
    </div>
  );
}

export default function AdminVerificationsPage() {
  const role = currentUserRole();
  const [pending, setPending] = useState<VerificationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  function refresh() {
    setLoading(true);
    getPendingVerifications()
      .then(setPending)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (role === 'admin') refresh();
    else setLoading(false);
  }, [role]);

  if (role !== 'admin') {
    return (
      <div className="page heroPage">
        <div className="container">
          <PageHeader
            kicker="Restricted"
            title="Admins only"
            subtitle="This area is reserved for SafariCo Hub administrators."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="page heroPage">
      <div className="container">
        <PageHeader
          kicker="Partner Verification"
          title="Review pending applications"
          subtitle="Approve or reject guides and operators who have applied to join the network."
        />

        {loading && <p className="muted">Loading applications…</p>}
        {error && <p className="error">{error}</p>}
        {!loading && pending.length === 0 && (
          <p className="muted">No pending applications right now.</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
          {pending.map((v) => (
            <Card key={v.verification_id} title={`${v.name} — ${v.role}`}>
              <p className="muted" style={{ margin: 0 }}>
                {v.email} · applied {new Date(v.verification_submitted_at).toLocaleDateString()}
              </p>

              {expandedId === v.verification_id ? (
                <ApplicationDetail
                  id={v.verification_id}
                  onDecided={() => {
                    setExpandedId(null);
                    refresh();
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="btn btnSecondary"
                  style={{ marginTop: 12 }}
                  onClick={() => setExpandedId(v.verification_id)}
                >
                  View application
                </button>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
